import React, { useMemo } from 'react';
import {
  Switch,
  Route,
  BrowserRouter,
  Redirect,
  matchPath,
} from 'react-router-dom';
import { isMobileOnly } from 'react-device-detect';

import ProtectedRoute from '../../utils/ProtectedRoute';
import AnchorRoute from '../../utils/AnchorRoute';
import UserContextRoute from '../../utils/UserContextRoute';
import HomePage from '../../pages/Home/Home';
import BlogPage from '../../pages/Blogs/Blog/Blog';
import NewsPage from '../../pages/News/News';
import AnchorsPage from '../../pages/Anchors/Anchors';
import ArchivesPage from '../../pages/Archives/Archives';
import AuditionsPage from '../../pages/Auditions/Auditions';
import ExternalPartnerDiscountPage from '../../pages/DiscountPage/ExternalPartnerDiscountPage';
import CreateAccountPage from '../../pages/CreateAccount/CreateAccount';
import CheckoutPage from '../../pages/Checkout/Checkout';
import CookiePolicyPage from '../../pages/CookiesPolicy/CookiesPolicy';
import PrivacyPolicyPage from '../../pages/PrivacyPolicy/PrivacyPolicy';
import CustomerServicePage from '../../pages/CustomerService/CustomerService';
import FAQPage from '../../pages/FAQ/FAQ';
import CookieSettingsPage from '../../pages/CookiesSettings/CookiesSettings';
import TermsAndConditionsPage from '../../pages/TermsAndConditions/TermsAndConditions';
import TwoTwoFiveSevenPage from '../../pages/TwoTwoFiveSevenPage';
import BillingSupportPage from '../../pages/BillingSupport/BillingSupport';
import ContactUs from '../../pages/ContactUs/ContactUs';
import ErrorPage from '../../pages/ErrorPage/ErrorPage';
import ManageAccountPage from '../../pages/ManageAccount/ManageAccount';
import MediaPage from '../../pages/Media/Media';
import ResetPasswordPage from '../../pages/PasswordManagement/ResetPassword';
import LoginPage from '../../pages/Login/Login';
import NewPasswordPage from '../../pages/PasswordManagement/NewPassword';
import AuthorizeDevicePage from '../../pages/AuthorizeDevice/AuthorizeDevice';
import SubscriptionsPage from '../../pages/ManageAccount/SubscriptionsPage';
import EmailPrefPage from '../../pages/ManageAccount/EmailPreferences';
import { UserContext } from '../../context';
import TourPage from '../../pages/TourPage';
import TourPageGifs from '../../pages/TourPageGifs';
import AnchorDashboard from '../../pages/AnchorDashboard/AnchorDashboard';
import AnchorSocialMediaSettings from '../../pages/AnchorDashboard/AnchorSocialMediaSettings/AnchorSocialMediaSettings';
import AnchorGalleries from '../../pages/AnchorDashboard/AnchorGalleries/AnchorGalleries';
import AnchorGallery from '../../pages/AnchorDashboard/AnchorGallery/AnchorGallery';
import AnchorVideosArchive from '../../pages/AnchorDashboard/AnchorVideosArchive/AnchorVideosArchive';
import PrivateMessaging from '../../pages/AnchorDashboard/PrivateMessaging/PrivateMessaging';
import MemberChat from '../../pages/MemberChat/MemberChat';
import CampaignOverlay from '../../pages/CampaignOverlay/CampaignOverlay';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import FanZone from '../../pages/FanZone/FanZone';
import CalendarPurchase from '../../pages/CalendarPurchase/CalendarPurchase';
import CalendarCheckout from '../../pages/CalendarCheckout/CalendarCheckout';
import OAuthLogin from '../../pages/OAuthLogin/OAuthLogin';
import SummerPackPurchase from '../../pages/SummerPackPurchase/SummerPackPurchase';
import TrendingPage from '../TrendingPage/TrendingPage';
import FeaturedContent from '../../pages/FeaturedContent/FeaturedContent';
import DiscountsPage from '../../pages/DiscountPage/DiscountsPage';
import ClipStorePage from '../../pages/ClipStore/ClipStorePage';
import { PROMO_CODES } from '../../constants/promoCodes';

import RoutesWithSlugPath from './RoutesWithSlugPath';
import AffiliateProgram from '../../pages/AffiliateProgram/AffiliateProgram';
import { isNakedNewsLiveDomain } from '../../utils/helpers';
import Cam4OAuthLogin from '../../pages/Cam4OAuth/Cam4OAuthLogin';
import LoginWithCAM4 from '../generalComponents/LoginWithCAM4';


const Routes = ({
  userPremiumExpired,
  showPremiumUpgradeBar,
  closePremiumUpgradeBar,
  activeCampaigns,
  anchorPortalCampaign,
  modalsData,
  refreshModalsData,
}) => {
  const user = React.useContext(UserContext);
  const isAnchor = user.user ? user.user.account.anchor : null;
  const footerUnavailable = isAnchor && isMobileOnly;
  const hasCalendar = user.features.includes('CALENDAR_2020');

  const setAtc = () => {
    document.cookie = '_atc=XPOSEDH; expires=0; path=/;';
  };

  const basename = process.env.REACT_APP_MR_SUBFOLDER ? process.env.REACT_APP_MR_SUBFOLDER : '';
  const homePage = isNakedNewsLiveDomain ? '/live' : '/home';

  return (
    <div id="application">
      <BrowserRouter basename={basename}>
        <>
          <Route
            render={(props) => {
              const isAnchorPortal = matchPath(
                props.location.pathname,
                '/dashboard'
              );
              const isAffiliatePage = matchPath(
                props.location.pathname, '/affiliates'
              )

              return (
                <Header
                  activeCampaign={activeCampaigns?.find((c) => c.hasTimer !== false)}
                  closePremiumUpgradeBar={closePremiumUpgradeBar}
                  isAnchorPortal={isAnchorPortal}
                  modalsData={modalsData}
                  refreshModalsData={refreshModalsData}
                  showPremiumUpgradeBar={showPremiumUpgradeBar}
                  userPremiumExpired={userPremiumExpired}
                  isAffiliatePage={isAffiliatePage}
                  {...props}
                />
              );
            }}
          />
          <Route
            render={(props) => (
              <CampaignOverlay
                matchParam="?_atc=ROMIRAINV"
                purchaseUrl="/account/purchase?_atc=ROMIRAINV"
                shouldBeAuthenticated={false}
                user={user}
                {...props}
              />
            )}
          />
          <Switch>
            <Route exact={true} path="/dashboard/broadcast">
              <Redirect to="/" />
            </Route>

            <Route
              path="/dashboard"
              render={({ match: { url } }) => (
                <>
                  <AnchorRoute
                    component={AnchorDashboard}
                    exact={true}
                    path={`${url}/`}
                  />
                  <AnchorRoute
                    component={AnchorGalleries}
                    exact={true}
                    path={`${url}/galleries`}
                  />
                  <AnchorRoute
                    component={AnchorSocialMediaSettings}
                    exact={true}
                    path={`${url}/social-media-settings`}
                  />
                  <AnchorRoute
                    component={AnchorGallery}
                    exact={true}
                    path={`${url}/galleries/:galleryId`}
                  />
                  <AnchorRoute
                    component={AnchorVideosArchive}
                    exact={true}
                    path={`${url}/videos/:id?`}
                  />
                  <AnchorRoute
                    component={PrivateMessaging}
                    exact={true}
                    path={`${url}/private-chat`}
                  />
                </>
              )}
            />

            <Route
              path="/chat"
              render={() => {
                const isWhitelisted = user ? user.features.includes('ANCHOR_PORTAL') : false;

                return isWhitelisted ? (
                  <ProtectedRoute
                    component={MemberChat}
                    exact={true}
                    path="/chat"
                    user={user}
                  />
                ) : (
                  <Redirect to="/" />
                );
              }}
            />
            <Route exact={true} path="/">
              <Redirect to={homePage} />
            </Route>
            <UserContextRoute
              activeCampaign={activeCampaigns?.find((c) => c.mainImage !== null) || {}}
              component={HomePage}
              exact={true}
              path="/home"
            />
            <UserContextRoute
              component={Cam4OAuthLogin}
              exact={true}
              path="/cam4oauth"
            />
            <Route
              exact={true}
              path="/cam4/login"
              render={() => <LoginWithCAM4 user={user} shouldDoLogin={true} />}
            />
            <Route exact={true} path="/blog">
              <Redirect to="/news" />
            </Route>
            <Route exact={true} path="/blog/category/:id">
              <Redirect to="/news/category/:id" />
            </Route>

            <Route exact={true} path="/blog/tag/:tag">
              <Redirect to="/news/tag/:tag" />
            </Route>
            <UserContextRoute
              component={ClipStorePage}
              exact={true}
              path="/clip-store"
            />
            <UserContextRoute
              component={AnchorsPage}
              exact={true}
              path="/naked-news-anchors"
            />
            <UserContextRoute
              component={AuditionsPage}
              exact={true}
              path="/auditions"
            />
            <UserContextRoute component={NewsPage} exact={true} path="/news" />
            <UserContextRoute
              component={NewsPage}
              exact={true}
              path="/news/category/:id"
            />
            <UserContextRoute
              component={NewsPage}
              exact={true}
              path="/news/tag/:tag"
            />

            <Route
              component={CookiePolicyPage}
              exact={true}
              path="/cookie-policy"
              strict={true}
            />
            <Route
              component={PrivacyPolicyPage}
              exact={true}
              path="/privacy_policy"
              strict={true}
            />
            <Route
              component={CustomerServicePage}
              exact={true}
              path="/privacy_policy/3rd-parties"
              strict={true}
            />
            <Route component={FAQPage} exact={true} path="/faq" strict={true} />
            <Route
              component={CookieSettingsPage}
              exact={true}
              path="/cookie-settings"
              strict={true}
            />
            <Route
              component={TermsAndConditionsPage}
              exact={true}
              path="/terms_of_service"
              strict={true}
            />
            <Route
              component={TwoTwoFiveSevenPage}
              exact={true}
              path="/2257"
              strict={true}
            />
            <Route
              component={BillingSupportPage}
              exact={true}
              path="/billing/processing"
              strict={true}
            />
            <Route
              component={MediaPage}
              exact={true}
              path="/media"
              strict={true}
            />
            <Route
              component={ContactUs}
              exact={true}
              path="/contact"
              strict={true}
            />
            <ProtectedRoute
              component={ManageAccountPage}
              exact={true}
              path="/account"
            />
            <Route component={FanZone} exact={true} path="/fan-zone" />
            <Route
              component={TrendingPage}
              exact={true}
              path="/trending"
              strict={true}
            />
            {hasCalendar && (
              <Route
                component={CalendarPurchase}
                exact={true}
                path="/calendar-purchase"
              />
            )}
            <Route
              component={CalendarCheckout}
              exact={true}
              path="/calendar-checkout"
            />
            <Route
              component={SummerPackPurchase}
              exact={true}
              path="/summerpack-purchase"
            />
            <Route component={OAuthLogin} exact={true} path="/oauth2/login" />
            <UserContextRoute
              component={BlogPage}
              exact={true}
              path="/blog/:id"
            />
            <UserContextRoute
              component={ArchivesPage}
              exact={true}
              path="/archives"
            />
            <ProtectedRoute
              component={ExternalPartnerDiscountPage}
              exact={true}
              path="/member-specials"
            />
            <Route
              exact={true}
              path="/account/purchase"
              render={({ match, location, history }) => (
                <CreateAccountPage
                  activeCampaign={activeCampaigns?.find((c) => c.affiliateCode === '4JULY2023') || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  isAccountCreationPage={true}
                  listOfActiveCampaigns={activeCampaigns.filter((c) => c.affiliateCode !== PROMO_CODES.TRIALFANCLUBS) || []}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/lifetime-promo-live"
              render={({ match, location, history }) => (
                <CreateAccountPage
                  activeCampaign={activeCampaigns?.find((c) => c.affiliateCode === PROMO_CODES.NNLIVEMRSK4) || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/nnl-promo-card"
              render={({ match, location, history }) => (
                <CreateAccountPage
                  activeCampaign={activeCampaigns?.[0] || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/nn-promo-card"
              render={({ match, location, history }) => (
                <CreateAccountPage
                  activeCampaign={activeCampaigns?.[0] || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/promo/asn-awards"
              render={({ match, location, history }) => (
                <CreateAccountPage
                  activeCampaign={activeCampaigns?.find((c) => c.affiliateCode === PROMO_CODES.ASNAW22) || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/2023-calendar-promo"
              render={({ match, location, history }) => (
                <DiscountsPage
                  activeCampaign={activeCampaigns?.find((c) => c.affiliateCode === PROMO_CODES.TRIALFANCLUBS) || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  isCyberMondayPromo={false}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/2022-cyber-monday-promo"
              render={({ match, location, history }) => (
                <DiscountsPage
                  activeCampaign={activeCampaigns?.find((c) => c.affiliateCode === PROMO_CODES.CYBERMONDAY2022) || {}}
                  anchorPortalCampaign={anchorPortalCampaign}
                  history={history}
                  isCyberMondayPromo={true}
                  location={location}
                  match={match}
                  user={user}
                />
              )}
            />
            <Route
              exact={true}
              path="/portal"
              render={() => {
                if (user.user && user.user.account.anchor) {
                  return <Redirect to={{ pathname: '/dashboard' }} />;
                }

                return <ErrorPage />;
              }}
            />
            <UserContextRoute
              component={CheckoutPage}
              exact={true}
              path="/checkout"
            />
            <Route
              component={ResetPasswordPage}
              exact={true}
              path="/account/password/new"
            />
            <Route
              component={NewPasswordPage}
              path="/account/password/reset/:token"
            />
            <Route component={LoginPage} exact={true} path="/account/login" />
            <Route
              component={AuthorizeDevicePage}
              exact={true}
              path="/account/devices"
            />
            <ProtectedRoute
              component={SubscriptionsPage}
              exact={true}
              path="/account/subscriptions"
            />
            <ProtectedRoute
              component={EmailPrefPage}
              exact={true}
              path="/account/email_preferences"
            />
            <Route component={TourPageGifs} exact={true} path="/tour/gifs" />
            <Route
              exact={true}
              path="/tour"
              render={(props) => {
                if (!window.location.hostname.includes('nakednews') && !user.authenticated) {
                  return <TourPage {...props} />;
                }

                return <Redirect to="/" />;
              }}
            />
            <Route
              path="/hollywood"
              render={() => {
                setAtc();

                return <Redirect to={'/archives?segmentid=219'} />;
              }}
            />
            <Route
              path="/affiliates"
              exact={true}
              component={AffiliateProgram}
            />
            <Route
              exact={true}
              path="/naked-amateur-auditions/:slug*"
              render={({ match, location }) => (
                <AuditionsPage
                  location={location}
                  slug={match.params.slug}
                  user={user}
                />
              )}
            />
            <Route
              component={FeaturedContent}
              exact={true}
              path="/featured-content"
            />
            <Route component={ErrorPage} exact={true} path="/not-found" />
            <RoutesWithSlugPath
              activeCampaign={activeCampaigns?.[0] || {}}
              anchorPortalCampaign={anchorPortalCampaign}
            />
          </Switch>
          {!footerUnavailable && (
            <Route render={(props) => <Footer {...props} />} />
          )}
        </>
      </BrowserRouter>
    </div>
  );
};

export default Routes;
